import { FC, useEffect, useState } from "react";
import * as d3 from "d3";
import * as am5 from "@amcharts/amcharts5";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
//Type of the chart
import * as am5xy from "@amcharts/amcharts5/xy";
import { MinMaxUnit } from "../../../pages/simpleapp/components/SimpleAppTemplate";
import {
  ColorVariables,
  getChartDateTime,
} from "../../../pages/simpleapp/components/widgets/WidgetHelper";
import { ColumnLineMultiHistoricalData } from "../../../pages/simpleapp/components/widgets/historic/hooks/useHistoricalColumnLineMulti";

type DataModel = {
  time: string;
  column: number;
  line: number;
};

type Props = {
  data: ColumnLineMultiHistoricalData[];
  columnLabel: string;
  lineLabel: string;
  chartId: string;
};

const AMCColumnLineMulti: FC<Props> = ({ data, columnLabel, lineLabel, chartId }) => {
  const [normalizedData, setNormalizedData] = useState<DataModel[]>([]);

  // Default
  const [columnData, setColumnData] = useState<MinMaxUnit>({ min: 0, max: 100, unit: "" });
  const [lineData, setLineData] = useState<MinMaxUnit>({ min: 0, max: 100, unit: "" });

  useEffect(() => {
    if (data) {
      setColumnData({ min: 0, max: 100, unit: data[0].columnDisplayUnit });
      setLineData({ min: 0, max: 100, unit: data[0].lineDisplayUnit });
      const dataArr: DataModel[] = [];

      data.map((datum) => {
        dataArr.push({
          time: getChartDateTime(datum.time),
          column: datum.column,
          line: datum.line,
        });
      });

      setNormalizedData(dataArr);
    }
  }, [data]);

  useEffect(() => {
    if (!!chartId && !!normalizedData && normalizedData.length > 0) {
      const root = am5.Root.new(chartId);

      // Set themes (https://www.amcharts.com/docs/v5/concepts/themes/)
      root.setThemes([am5themes_Animated.new(root)]);

      // Create Chart (https://www.amcharts.com/docs/v5/charts/xy-chart/)
      const chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          panX: true,
          panY: false,
          wheelY: "zoomX",
          layout: root.verticalLayout,
        }),
      );

      // Add cursor: In here you can configure how the cursor is displayed and how it behaves on the chart.
      const cursor = chart.set(
        "cursor",
        am5xy.XYCursor.new(root, {
          behavior: "none",
        }),
      );
      cursor.lineY.setAll({
        stroke: am5.color(ColorVariables.colorPlaceholder),
        visible: true,
      });
      cursor.lineX.setAll({
        stroke: am5.color(ColorVariables.colorPlaceholder),
        visible: true,
      });

      // Create axes https://www.amcharts.com/docs/v5/charts/xy-chart/axes/
      // For the X-Axis
      const xAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(root, {
          categoryField: "time",
          marginTop: 15,
          maxDeviation: 0,
          renderer: am5xy.AxisRendererX.new(root, {}),
        }),
      );
      xAxis.data.setAll(normalizedData);

      //### For the Column ###//
      //Left Axis
      const columnYAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          // min: 0,
          numberFormat: `#,###'${columnData.unit}'`,
          renderer: am5xy.AxisRendererY.new(root, {}),
        }),
      );
      columnYAxis.get("renderer").grid.template.set("forceHidden", true);

      //Column Bar - the data
      const columnSeries = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          name: columnLabel,
          xAxis: xAxis,
          yAxis: columnYAxis,
          valueYField: "column",
          categoryXField: "time",
          tooltip: am5.Tooltip.new(root, {
            labelText: `[bold]${columnLabel}[/]: {valueY}${columnData.unit} \n\n[bold]DateTime[/]: {categoryX}`,
            dy: -5,
          }),
        }),
      );
      columnSeries.columns.template.setAll({
        stroke: am5.color(ColorVariables.colorBlueWaterLight),
        fill: am5.color(ColorVariables.colorBlueWaterLight),
        width: am5.percent(60),
        cornerRadiusTL: 10,
        cornerRadiusTR: 10,
      });
      columnSeries.data.setAll(normalizedData);
      columnSeries.appear(1000);

      // create hover state for series and for mainContainer, so that when series is hovered,
      // the state would be passed down to the strokes which are in mainContainer.
      columnSeries.set("setStateOnChildren", true);
      columnSeries.states.create("hover", {});

      columnSeries.mainContainer.set("setStateOnChildren", true);
      columnSeries.mainContainer.states.create("hover", {});

      columnSeries.columns.template.states.create("hover", {
        strokeWidth: 3,
      });

      //### For the Line ###//
      //Left Axis
      const lineYAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          // min: 0,
          numberFormat: `#,###'${lineData.unit}'`,
          renderer: am5xy.AxisRendererY.new(root, { opposite: true }),
        }),
      );
      lineYAxis.get("renderer").grid.template.set("forceHidden", true);

      //Line Series - the data
      const lineSeries = chart.series.push(
        am5xy.LineSeries.new(root, {
          name: lineLabel,
          xAxis: xAxis,
          yAxis: lineYAxis,
          valueYField: "line",
          categoryXField: "time",
          curveFactory: d3.curveBumpX,
          tooltip: am5.Tooltip.new(root, {
            labelText: `[bold]${lineLabel}[/]: {valueY}${lineData.unit} \n\n[bold]DateTime[/]: {categoryX}`,
            dy: -5,
          }),
        }),
      );
      lineSeries.strokes.template.setAll({
        stroke: am5.color(ColorVariables.colorBlueWaterLight),
        strokeWidth: 1,
      });
      lineSeries.data.setAll(normalizedData);
      lineSeries.appear(1000);

      // create hover state for series and for mainContainer, so that when series is hovered,
      // the state would be passed down to the strokes which are in mainContainer.
      lineSeries.set("setStateOnChildren", true);
      lineSeries.states.create("hover", {});

      lineSeries.mainContainer.set("setStateOnChildren", true);
      lineSeries.mainContainer.states.create("hover", {});

      lineSeries.strokes.template.states.create("hover", {
        strokeWidth: 3,
      });

      // Add scrollbar
      const scrollbar = chart.set(
        "scrollbarX",
        am5.Scrollbar.new(root, {
          orientation: "horizontal",
          start: 0.8,
          height: 10,
          marginTop: 15,
        }),
      );
      scrollbar.thumb.setAll({
        fill: am5.color(ColorVariables.colorPlaceholder),
      });
      scrollbar.startGrip.setAll({
        visible: false,
      });
      scrollbar.endGrip.setAll({
        visible: false,
      });
      chart.bottomAxesContainer.children.push(scrollbar);

      // Add Legends
      const legend = chart.children.push(
        am5.Legend.new(root, {
          centerX: am5.p50,
          x: am5.p50,
        }),
      );

      // Make series change state when legend item is hovered
      legend.itemContainers.template.states.create("hover", {});

      legend.itemContainers.template.events.on("pointerover", function (e) {
        const target: any = e.target.dataItem?.dataContext;
        target.hover();
      });
      legend.itemContainers.template.events.on("pointerout", function (e) {
        const target: any = e.target.dataItem?.dataContext;
        target.unhover();
      });

      legend.data.setAll(chart.series.values);

      // Make stuff animate on load (https://www.amcharts.com/docs/v5/concepts/animations/)
      chart.appear(1000, 100);

      return () => {
        root.dispose();
      };
    }
  }, [chartId, normalizedData]);

  return <div className="am-chart-container" id={chartId} />;
};

export default AMCColumnLineMulti;
